/**
 * SectionReviewBar — three-state section review.
 *
 * State derived from:
 *   unassigned → section has no department_id
 *   assigned   → section has department_id, no compliance_signal yet
 *   reviewed   → compliance_signal is set (yes / partial / no / na)
 *
 * Props:
 *   rfpId        : string
 *   sectionIndex : number
 *   departmentId : string | null  (from section.department_id)
 */
import React, { useCallback, useState } from "react";
import { CheckCheck, Minus, XCircle, Loader2, MessageSquare, Building2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { v2Api } from "@/lib/v2Api";
import { useRfpOptional } from "@/contexts/RfpContext";


const SIGNAL_META = {
  yes:     { label: "Yes",     color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCheck },
  partial: { label: "Partial", color: "bg-amber-100 text-amber-800 border-amber-300",     icon: Minus },
  no:      { label: "No",      color: "bg-rose-100 text-rose-800 border-rose-300",         icon: XCircle },
  na:      { label: "N/A",     color: "bg-slate-100 text-slate-600 border-slate-300",      icon: Minus },
};

const STATE_META = {
  unassigned: { label: "Unassigned", color: "bg-slate-100 text-slate-500" },
  assigned:   { label: "Assigned",   color: "bg-blue-100 text-blue-700" },
  reviewed:   { label: "Reviewed",   color: "bg-emerald-100 text-emerald-700" },
};


export default function SectionReviewBar({ rfpId, sectionIndex, departmentId }) {
  const ctx = useRfpOptional();

  const reviews          = ctx?.sectionReviews || {};
  const review           = sectionIndex != null ? reviews[sectionIndex] : null;
  const setSectionReview = ctx?.setSectionReview;
  const departments      = ctx?.departments || [];
  const canManageRfp     = ctx?.canManageRfp || false;
  const isDeptLead       = ctx?.isDeptLead || false;
  const canReassignDept  = canManageRfp || isDeptLead;

  const [savingSignal, setSavingSignal] = useState(null);
  const [editingNote, setEditingNote]   = useState(false);
  const [noteText, setNoteText]         = useState(review?.comment || "");
  const [savingNote, setSavingNote]     = useState(false);

  // Dept picker state
  const [editingDept, setEditingDept]   = useState(false);
  const [selectedDept, setSelectedDept] = useState(departmentId || "");
  const [savingDept, setSavingDept]     = useState(false);

  // Derive 3-state
  const currentSignal = review?.compliance_signal ?? null;
  const sectionState = currentSignal
    ? "reviewed"
    : departmentId
      ? "assigned"
      : "unassigned";
  const stateMeta = STATE_META[sectionState];

  const handleSignal = useCallback(async (sig) => {
    if (savingSignal || sectionIndex == null) return;
    setSavingSignal(sig);
    try {
      const updated = await v2Api.rfp.upsertSectionReview(rfpId, sectionIndex, {
        compliance_signal: sig,
        comment: review?.comment ?? null,
      });
      setSectionReview?.(sectionIndex, updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not save compliance");
    } finally {
      setSavingSignal(null);
    }
  }, [rfpId, sectionIndex, savingSignal, review, setSectionReview]);

  const handleSaveDept = useCallback(async () => {
    if (sectionIndex == null) return;
    setSavingDept(true);
    try {
      await v2Api.rfp.updateSectionDept(rfpId, sectionIndex, selectedDept || null);
      setEditingDept(false);
      toast.success("Department updated");
      // Refresh RFP so sections array reflects new department_id
      ctx?.refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not update department");
    } finally {
      setSavingDept(false);
    }
  }, [rfpId, sectionIndex, selectedDept, ctx]);

  const handleSaveNote = useCallback(async () => {
    if (sectionIndex == null) return;
    setSavingNote(true);
    try {
      const updated = await v2Api.rfp.upsertSectionReview(rfpId, sectionIndex, {
        compliance_signal: currentSignal,
        comment: noteText.trim() || null,
      });
      setSectionReview?.(sectionIndex, updated);
      setEditingNote(false);
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    } finally {
      setSavingNote(false);
    }
  }, [rfpId, sectionIndex, currentSignal, noteText, setSectionReview]);

  if (sectionIndex == null) return null;

  return (
    <div className="border-t mt-3 pt-3 space-y-2">

      {/* State badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={`text-[11px] ${stateMeta.color}`}>
          {stateMeta.label}
        </Badge>
        {currentSignal && (() => {
          const sm = SIGNAL_META[currentSignal];
          const Icon = sm.icon;
          return (
            <Badge className={`text-[11px] border ${sm.color} gap-1`}>
              <Icon className="w-3 h-3" />
              {sm.label}
            </Badge>
          );
        })()}
        <div className="ml-auto flex items-center gap-1">
          {canReassignDept && (
            <Button size="sm" variant="ghost"
              onClick={() => { setEditingDept((v) => !v); setSelectedDept(departmentId || ""); }}
              className="h-7 text-xs gap-1">
              <Building2 className="w-3 h-3" />
              {departmentId ? "Change dept" : "Assign dept"}
            </Button>
          )}
          <Button size="sm" variant="ghost"
            onClick={() => { setEditingNote((v) => !v); setNoteText(review?.comment || ""); }}
            className="h-7 text-xs gap-1">
            <MessageSquare className="w-3 h-3" />
            {review?.comment ? "Edit note" : "Add note"}
          </Button>
        </div>
      </div>

      {/* Dept picker */}
      {editingDept && (
        <div className="flex items-center gap-2">
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">— Unassigned —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <Button size="sm" variant="ghost" disabled={savingDept}
            onClick={() => { setEditingDept(false); setSelectedDept(departmentId || ""); }}>
            Cancel
          </Button>
          <Button size="sm" disabled={savingDept} onClick={handleSaveDept}>
            {savingDept && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Save
          </Button>
        </div>
      )}

      {/* Compliance chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-slate-500 shrink-0">Compliance:</span>
        {Object.entries(SIGNAL_META).map(([sig, meta]) => {
          const Icon = meta.icon;
          const active  = currentSignal === sig;
          const loading = savingSignal === sig;
          return (
            <button key={sig} onClick={() => handleSignal(sig)}
              disabled={!!savingSignal}
              className={`h-6 px-2 text-[11px] rounded border flex items-center gap-1 transition-colors
                ${active
                  ? meta.color + " font-medium"
                  : "border-slate-200 text-slate-500 hover:border-slate-400 disabled:opacity-50"
                }`}>
              {loading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Icon className="w-3 h-3" />}
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* Note display */}
      {review?.comment && !editingNote && (
        <p className="text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2">
          {review.comment}
        </p>
      )}

      {/* Note editor */}
      {editingNote && (
        <div className="space-y-2">
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Reviewer note…" rows={3} maxLength={2000} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" disabled={savingNote}
              onClick={() => { setEditingNote(false); setNoteText(review?.comment || ""); }}>
              Cancel
            </Button>
            <Button size="sm" disabled={savingNote} onClick={handleSaveNote}>
              {savingNote && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Save note
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
